import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $createTextNode,
  FORMAT_TEXT_COMMAND,
} from 'lexical';
import { $patchStyleText } from '@lexical/selection';
import { $createBeautifulMentionNode } from 'lexical-beautiful-mentions';
import { serializeToWire } from '../serialize';
import { $createColorTextNode, $isColorTextNode } from '../nodes/ColorTextNode';

/**
 * Exposes an imperative handle on `apiRef` so ChatPanel's existing toolbar/send
 * handlers can drive the editor without owning its internal state:
 *
 *   serialize()          -> current wire string (untrimmed)
 *   clear()              -> empty the editor + refocus
 *   focus()
 *   insertText(str)      -> insert at caret (emoji)
 *   applyFormat(type)    -> toggle 'bold'|'italic'|'underline' on the selection
 *   applyColor(hex)      -> color the selected range inline ([#hex|…])
 *   hasSelection()       -> true if a non-collapsed range exists
 *
 * `onRegister` (optional) is called with the handle on mount and `null` on
 * unmount, so the ComposerContext can reach the editor without a shared ref.
 */
export default function EditorRefPlugin({ apiRef, onRegister }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handle = {
      serialize() {
        return editor.getEditorState().read(() => serializeToWire($getRoot()));
      },

      clear() {
        editor.update(() => {
          const root = $getRoot();
          root.clear();
          const p = $createParagraphNode();
          root.append(p);
          p.select();
        });
      },

      focus() {
        editor.focus();
      },

      insertText(str) {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertText(str);
          } else {
            // No selection (e.g. never focused): append to the end.
            const root = $getRoot();
            const last = root.getLastDescendant();
            if (last) {
              last.selectEnd();
              const sel = $getSelection();
              if ($isRangeSelection(sel)) sel.insertText(str);
            }
          }
        });
        editor.focus();
      },

      // Insert a mention pill at the caret (used by the RightSidebar row menu).
      // Mirrors what the autocomplete produces: a BeautifulMentionNode carrying
      // the same `data` ({ id, usernameColor, avatar }) so it renders in the
      // user's color and serializes to `@[value](id)` — identical to a typed
      // mention. `value` is the display username; `data.id` the stable userId.
      insertMention(value, data) {
        if (!value) return;
        editor.update(() => {
          let selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            // No live caret (menu click never focused the editor): drop one at
            // the end so the pill lands somewhere sensible. Prefer the last
            // descendant; on an empty editor fall back to the root end.
            const root = $getRoot();
            const last = root.getLastDescendant();
            if (last) last.selectEnd();
            else root.selectEnd();
            selection = $getSelection();
            if (!$isRangeSelection(selection)) return;
          }
          const mention = $createBeautifulMentionNode('@', value, data);
          // Trailing space so the next typed word doesn't fuse onto the pill —
          // matches the autocomplete's auto-space behaviour.
          selection.insertNodes([mention, $createTextNode(' ')]);
        });
        editor.focus();
      },

      applyFormat(type) {
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, type);
      },

      applyColor(hex) {
        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || selection.isCollapsed()) return;
          // Convert the selected text nodes to ColorTextNodes carrying __color,
          // preserving their format flags. $patchStyleText splits at the range
          // boundaries for us; we then upgrade the resulting nodes in-place.
          $patchStyleText(selection, { color: hex });
          for (const node of selection.getNodes()) {
            if (node.getType() === 'text') {
              const colorNode = $createColorTextNode(node.getTextContent(), hex);
              colorNode.setFormat(node.getFormat());
              node.replace(colorNode);
            } else if ($isColorTextNode(node)) {
              node.setColor(hex);
            }
          }
        });
        editor.focus();
      },

      hasSelection() {
        return editor.getEditorState().read(() => {
          const selection = $getSelection();
          return $isRangeSelection(selection) && !selection.isCollapsed();
        });
      },
    };

    if (apiRef) apiRef.current = handle;
    if (onRegister) onRegister(handle);

    return () => {
      if (apiRef) apiRef.current = null;
      if (onRegister) onRegister(null);
    };
  }, [editor, apiRef, onRegister]);

  return null;
}
